// Coach Analytics-Deepdive — tile bodies 1–11 (Phase 4e). Tile 12 lives in ReportsTile.jsx.
import { useState } from "react";
import { useT } from "@/i18n";
import {
  Users, Activity, Calendar, Package, Wallet, Clock, CheckCircle2, XCircle, PieChart, Hourglass, Gauge,
  Eye, FileText, Image as ImageIcon, Heart, Gift, MessageCircle, Flag, Trophy, Lightbulb, RefreshCw, Tags, Star, Send, ClipboardCheck, Dumbbell, MapPin,
} from "lucide-react";
import { MiniStat, MiniGrid, SectionLabel, EmptyLine, BarRow, Chip, fmtEur, fmtMonthSince } from "./shared";

const DOW = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

// Vertical paired-bar mini chart.
function MiniBars({ rows, keys, colors, labelFn, height = 80 }) {
  const max = Math.max(1, ...rows.flatMap((r) => keys.map((k) => Math.abs(r[k] || 0))));
  return (
    <div className="flex items-end gap-1.5" style={{ height }}>
      {rows.map((r, i) => (
        <div key={i} className="flex flex-1 flex-col items-center gap-1">
          <div className="flex w-full items-end justify-center gap-0.5" style={{ height: height - 16 }}>
            {keys.map((k, ki) => (
              <div key={k} className={`w-2 rounded-t ${colors[ki]}`} style={{ height: `${Math.max(2, (Math.abs(r[k] || 0) / max) * 100)}%` }} />
            ))}
          </div>
          <span className="text-[9px] text-muted-foreground">{labelFn(r)}</span>
        </div>
      ))}
    </div>
  );
}

export function ProfileTile({ profile }) {
  const t = useT();
  const c = profile.clients || {};
  return (
    <div>
      <div className="mb-3 rounded-2xl border border-primary/30 bg-primary/5 p-4">
        <p className="text-xs text-muted-foreground">{t("an.t1_clients")}</p>
        <p className="font-display text-3xl font-semibold text-primary">{c.total || 0}</p>
        <div className="mt-2 flex flex-wrap gap-1.5">
          <Chip label={`${t("an.t1_ic")}: ${c.inner_circle || 0}`} />
          <Chip label={`${t("an.t1_lc")}: ${c.local_circle || 0}`} />
          <Chip label={`${t("an.t1_general")}: ${c.general || 0}`} />
        </div>
      </div>
      <MiniGrid>
        <MiniStat icon={Calendar} label={t("an.t1_sessions")} value={profile.sessions_completed || 0} />
        <MiniStat icon={Package} label={t("an.t1_offerings")} value={profile.active_offerings || 0} />
      </MiniGrid>
      <MiniGrid>
        <MiniStat icon={Wallet} label={t("an.t1_gross")} value={fmtEur(profile.gross_total_cents || 0)} tone="success" />
        <MiniStat icon={Clock} label={t("an.t1_since")} value={fmtMonthSince(profile.member_since)} />
      </MiniGrid>
    </div>
  );
}

export function GrowthTile({ growth }) {
  const t = useT();
  const rows = growth.rows || [];
  return (
    <div>
      <MiniBars rows={rows} keys={["new", "churn"]} colors={["bg-primary", "bg-amber-500"]} labelFn={(r) => r.month.slice(-2)} />
      <div className="mt-2 flex justify-center gap-4 text-xs">
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-primary" /> {t("an.t2_new")}</span>
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-amber-500" /> {t("an.t2_churn")}</span>
      </div>
      {rows.slice(-3).map((r) => (
        <div key={r.month} className="flex items-center gap-3 border-t border-border py-1.5 text-xs">
          <span className="w-16 font-medium text-foreground">{r.month}</span>
          <span className="text-primary">+{r.new}</span>
          <span className="text-amber-600">-{r.churn}</span>
          <span className="flex-1 text-muted-foreground">{t("an.t2_net")} {r.net >= 0 ? "+" : ""}{r.net}</span>
          <span className="text-muted-foreground">{t("an.t2_cumulative")}: {r.cumulative}</span>
        </div>
      ))}
    </div>
  );
}

export function SessionTile({ sessions }) {
  const t = useT();
  const maxH = Math.max(1, ...(sessions.popular_hours || []).map((h) => h.count));
  const maxD = Math.max(1, ...(sessions.popular_weekdays || []).map((d) => d.count));
  return (
    <div>
      <MiniGrid>
        <MiniStat icon={Calendar} label={t("an.t3_total")} value={sessions.total_bookings || 0} />
        <MiniStat icon={CheckCircle2} label={t("an.t3_completed")} value={sessions.completed || 0} tone="success" />
      </MiniGrid>
      <MiniGrid>
        <MiniStat icon={XCircle} label={t("an.t3_cancelled")} value={sessions.cancelled || 0} tone="warning" />
        <MiniStat icon={PieChart} label={t("an.t3_cancel_rate")} value={`${Math.round((sessions.cancellation_rate || 0) * 100)}%`} />
      </MiniGrid>
      <MiniGrid>
        <MiniStat icon={Hourglass} label={t("an.t3_avg_dur")} value={`${sessions.avg_duration_min || 0} min`} />
        <MiniStat icon={Gauge} label={t("an.t3_util")} value={sessions.utilisation != null ? `${Math.round(sessions.utilisation * 100)}%` : "—"} />
      </MiniGrid>
      {(sessions.popular_hours || []).length > 0 && (
        <>
          <SectionLabel>{t("an.t3_hours")}</SectionLabel>
          <div className="flex items-end gap-1" style={{ height: 56 }}>
            {sessions.popular_hours.map((h) => (
              <div key={h.hour} className="flex flex-1 flex-col items-center gap-1">
                <div className="w-full rounded-t bg-primary" style={{ height: `${Math.max(2, (h.count / maxH) * 40)}px` }} />
                <span className="text-[8px] text-muted-foreground">{String(h.hour).padStart(2, "0")}</span>
              </div>
            ))}
          </div>
        </>
      )}
      {(sessions.popular_weekdays || []).length > 0 && (
        <>
          <SectionLabel>{t("an.t3_weekdays")}</SectionLabel>
          <div className="flex items-end gap-1" style={{ height: 56 }}>
            {sessions.popular_weekdays.map((d) => (
              <div key={d.weekday} className="flex flex-1 flex-col items-center gap-1">
                <div className="w-full rounded-t bg-emerald-500" style={{ height: `${Math.max(2, (d.count / maxD) * 40)}px` }} />
                <span className="text-[8px] text-muted-foreground">{DOW[d.weekday] || "?"}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export function WevaTile({ weva, emoDrop }) {
  const t = useT();
  if ((weva.client_count || 0) === 0) return <EmptyLine>{t("an.t4_empty")}</EmptyLine>;
  const maxEmo = Math.max(1, ...(weva.emotions || []).map((e) => e.count));
  const trend = weva.intensity_trend || [];
  const maxInt = Math.max(10, ...trend.map((tt) => Math.max(tt.avg_before || 0, tt.avg_after || 0)));
  return (
    <div>
      <MiniGrid>
        <MiniStat icon={Users} label={t("an.t4_client_count")} value={weva.client_count} />
        <MiniStat icon={FileText} label={t("an.t4_reports")} value={weva.reports_count || 0} />
      </MiniGrid>
      {(weva.emotions || []).length > 0 && (
        <>
          <SectionLabel>{t("an.t4_emotions")}</SectionLabel>
          {weva.emotions.slice(0, 6).map((e) => (
            <BarRow key={e.key} label={e.key} value={e.count} pct={Math.round((e.count / maxEmo) * 100)} />
          ))}
        </>
      )}
      {emoDrop && Array.isArray(emoDrop.rows) && emoDrop.rows.length > 0 && (
        <>
          <SectionLabel>{t("an.t4_emo_drop")}</SectionLabel>
          {emoDrop.rows.slice(0, 6).map((r) => (
            <BarRow key={r.emotion} testId={`an-emo-drop-${r.emotion}`} label={r.emotion} value={`−${r.avg_drop.toFixed(1)}`} pct={Math.max(5, Math.min(100, Math.round((r.avg_drop / 10) * 100)))} tone="success" />
          ))}
          <p className="mt-1 text-[10px] italic text-muted-foreground">{t("an.t4_emo_drop_hint", { n: emoDrop.total_sessions || 0 })}</p>
        </>
      )}
      {(weva.themes || []).length > 0 && (
        <>
          <SectionLabel>{t("an.t4_themes")}</SectionLabel>
          <div className="flex flex-wrap gap-1.5">
            {weva.themes.slice(0, 12).map((th) => <Chip key={th.key} label={th.key} count={th.count} />)}
          </div>
        </>
      )}
      {trend.length > 0 && (
        <>
          <SectionLabel>{t("an.t4_trend")}</SectionLabel>
          <div className="flex items-end gap-1.5" style={{ height: 60 }}>
            {trend.map((tt) => (
              <div key={tt.week} className="flex flex-1 flex-col items-center gap-1">
                <div className="flex w-full items-end justify-center gap-0.5" style={{ height: 44 }}>
                  <div className="w-2 rounded-t bg-amber-500" style={{ height: `${Math.max(2, (tt.avg_before / maxInt) * 100)}%` }} title={t("an.t4_before")} />
                  <div className="w-2 rounded-t bg-emerald-500" style={{ height: `${Math.max(2, (tt.avg_after / maxInt) * 100)}%` }} title={t("an.t4_after")} />
                </div>
                <span className="text-[8px] text-muted-foreground">{tt.week.slice(-3)}</span>
              </div>
            ))}
          </div>
          <div className="mt-1.5 flex justify-center gap-4 text-xs">
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-amber-500" /> {t("an.t4_before")}</span>
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-emerald-500" /> {t("an.t4_after")}</span>
          </div>
        </>
      )}
    </div>
  );
}

export function PlansTile({ plans }) {
  const t = useT();
  if ((plans.plans_sent || 0) === 0) return <EmptyLine>{t("an.t5_empty")}</EmptyLine>;
  return (
    <div>
      <MiniGrid>
        <MiniStat icon={Send} label={t("an.t5_sent")} value={plans.plans_sent || 0} />
        <MiniStat icon={Eye} label={t("an.t5_open_rate")} value={`${Math.round((plans.open_rate || 0) * 100)}%`} tone="success" />
      </MiniGrid>
      <MiniGrid>
        <MiniStat icon={CheckCircle2} label={t("an.t5_completion")} value={`${Math.round((plans.completion_rate || 0) * 100)}%`} tone="success" />
        <MiniStat icon={Dumbbell} label={t("an.t5_sessions_done")} value={plans.session_completions || 0} />
      </MiniGrid>
      {(plans.top_techniques || []).length > 0 && (
        <>
          <SectionLabel>{t("an.t5_top_tech")}</SectionLabel>
          <div className="flex flex-wrap gap-1.5">
            {plans.top_techniques.map((tt) => <Chip key={tt.technique} label={String(tt.technique).replace(/_/g, " ")} count={tt.count} />)}
          </div>
        </>
      )}
    </div>
  );
}

export function ContentTile({ content }) {
  const t = useT();
  if ((content.posts_total || 0) === 0 && (content.content_purchases || 0) === 0) return <EmptyLine>{t("an.t6_empty")}</EmptyLine>;
  return (
    <div>
      <MiniGrid>
        <MiniStat icon={ImageIcon} label={t("an.t6_posts")} value={content.posts_total || 0} />
        <MiniStat icon={Eye} label={t("an.t6_views")} value={content.posts_views || 0} />
      </MiniGrid>
      <MiniGrid>
        <MiniStat icon={Heart} label={t("an.t6_favorites")} value={content.posts_favorites || 0} />
        <MiniStat icon={Wallet} label={t("an.t6_revenue")} value={fmtEur(content.content_revenue_cents || 0)} tone="success" />
      </MiniGrid>
      {(content.top_items || []).length > 0 && (
        <>
          <SectionLabel>{t("an.t6_top")}</SectionLabel>
          {content.top_items.slice(0, 5).map((it) => (
            <div key={it.id} className="flex items-center gap-2 border-t border-border py-1.5">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-foreground">{it.title}</p>
                <p className="text-xs text-muted-foreground">{it.kind} · {it.views}👁 · {it.favorites}♡</p>
              </div>
              <span className="text-sm font-medium text-emerald-600">{fmtEur(it.revenue_cents || 0)}</span>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

export function CirclesTile({ circles }) {
  const t = useT();
  const ic = circles.inner_circle || {};
  const lc = circles.local_circle || {};
  if ((ic.circles || 0) === 0 && (lc.circles || 0) === 0) return <EmptyLine>{t("an.t7_empty")}</EmptyLine>;
  const Row = ({ label, value }) => (
    <div className="flex items-center justify-between py-0.5 text-xs"><span className="text-muted-foreground">{label}</span><span className="font-medium text-foreground">{value}</span></div>
  );
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      <div className="rounded-xl border border-primary/30 bg-primary/5 p-3">
        <p className="mb-1.5 flex items-center gap-1.5 text-sm font-semibold text-primary"><Star className="h-3.5 w-3.5" /> {t("an.t7_ic")}</p>
        <Row label={t("an.t7_members")} value={ic.members || 0} />
        <Row label={t("an.t7_revenue")} value={fmtEur(ic.revenue_cents || 0)} />
        <Row label={t("an.t7_messages")} value={ic.messages || 0} />
        <Row label={t("an.t7_snapshots")} value={ic.snapshots_delivered || 0} />
      </div>
      <div className="rounded-xl border border-emerald-300/50 bg-emerald-50/50 p-3">
        <p className="mb-1.5 flex items-center gap-1.5 text-sm font-semibold text-emerald-700"><MapPin className="h-3.5 w-3.5" /> {t("an.t7_lc")}</p>
        <Row label={t("an.t7_members")} value={lc.members || 0} />
        <Row label={t("an.t7_messages")} value={lc.messages || 0} />
      </div>
    </div>
  );
}

export function AffiliateTile({ affiliate }) {
  const t = useT();
  if ((affiliate.total_referrals || 0) === 0) return <EmptyLine>{t("an.t8_empty")}</EmptyLine>;
  return (
    <div>
      <MiniGrid>
        <MiniStat icon={Users} label={t("an.t8_referrals")} value={affiliate.total_referrals || 0} />
        <MiniStat icon={Activity} label={t("an.t8_conversion")} value={`${Math.round((affiliate.conversion_rate || 0) * 100)}%`} tone="success" />
      </MiniGrid>
      <MiniGrid>
        <MiniStat icon={Wallet} label={t("an.t8_revenue")} value={fmtEur(affiliate.total_revenue_cents || 0)} tone="success" />
        <MiniStat icon={Gift} label={t("an.t8_active_phases")} value={affiliate.active_phases || 0} />
      </MiniGrid>
      {(affiliate.phase_breakdown || []).length > 0 && (
        <>
          <SectionLabel>{t("an.t8_phase_break")}</SectionLabel>
          <div className="flex flex-wrap gap-1.5">
            {affiliate.phase_breakdown.map((p) => <Chip key={p.phase} label={p.phase} count={p.count} />)}
          </div>
        </>
      )}
    </div>
  );
}

export function EngagementTile({ engagement }) {
  const t = useT();
  return (
    <div>
      <MiniGrid>
        <MiniStat icon={MessageCircle} label={t("an.t9_msgs")} value={engagement.messages_sent || 0} />
        <MiniStat icon={Clock} label={t("an.t9_response")} value={`${engagement.avg_response_minutes || 0} min`} />
      </MiniGrid>
      <MiniGrid>
        <MiniStat icon={CheckCircle2} label={t("an.t9_snap_delivered")} value={engagement.snapshots_delivered || 0} tone="success" />
        <MiniStat icon={Hourglass} label={t("an.t9_snap_pending")} value={engagement.snapshots_pending || 0} tone="warning" />
      </MiniGrid>
      {(engagement.circle_activity || []).length > 0 && (
        <>
          <SectionLabel>{t("an.t9_circle_activity")}</SectionLabel>
          {engagement.circle_activity.map((a) => (
            <div key={a.circle_type} className="flex items-center justify-between py-0.5 text-xs">
              <span className="capitalize text-muted-foreground">{a.circle_type}</span>
              <span className="font-medium text-foreground">{a.messages}</span>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

export function GoalsTile({ goals }) {
  const t = useT();
  const [show, setShow] = useState(false);
  if ((goals.client_count || 0) === 0) return <EmptyLine>{t("an.t10_empty")}</EmptyLine>;
  return (
    <div>
      <MiniGrid>
        <MiniStat icon={Users} label={t("an.t10_client_count")} value={goals.client_count || 0} />
        <MiniStat icon={Flag} label={t("an.t10_goals_total")} value={goals.goals_total || 0} />
      </MiniGrid>
      <MiniGrid>
        <MiniStat icon={Trophy} label={t("an.t10_goals_done")} value={goals.goals_completed || 0} tone="success" />
        <MiniStat icon={Lightbulb} label={t("an.t10_beliefs_total")} value={goals.beliefs_total || 0} />
      </MiniGrid>
      <MiniGrid>
        <MiniStat icon={RefreshCw} label={t("an.t10_beliefs_trans")} value={goals.beliefs_transformed || 0} tone="success" />
        <div className="flex-1 rounded-xl bg-secondary/60 p-3">
          <Tags className="h-4 w-4 text-foreground" />
          <p className="mt-1 text-[11px] text-muted-foreground">{t("an.t10_top_cats")}</p>
          <p className="text-sm font-medium text-foreground">{(goals.goal_categories || []).slice(0, 2).map((c) => c.category).join(", ") || "—"}</p>
        </div>
      </MiniGrid>
      <button onClick={() => setShow((v) => !v)} data-testid="an-goals-examples-toggle" className="mt-1 text-xs font-medium text-primary hover:underline">
        {show ? t("an.t10_hide_examples") : t("an.t10_show_examples")}
      </button>
      {show && (
        <div className="mt-2 rounded-xl bg-secondary/50 p-3">
          <p className="mb-2 text-[11px] italic text-muted-foreground">{t("an.t10_examples_note")}</p>
          {(goals.goal_examples || []).length > 0 && (
            <>
              <SectionLabel>{t("an.t10_goals")}</SectionLabel>
              {goals.goal_examples.map((e, i) => (
                <div key={`g-${i}`} className="mb-1.5 flex items-start gap-2">
                  <span className="shrink-0 rounded-full bg-primary/15 px-2 py-0.5 text-[10px] text-primary">{e.category}</span>
                  <span className="text-xs text-foreground">{e.text}</span>
                </div>
              ))}
            </>
          )}
          {(goals.belief_examples || []).length > 0 && (
            <>
              <SectionLabel>{t("an.t10_beliefs")}</SectionLabel>
              {goals.belief_examples.map((e, i) => (
                <div key={`b-${i}`} className="mb-1.5 flex items-start gap-2">
                  <span className="shrink-0 rounded-full bg-primary/15 px-2 py-0.5 text-[10px] text-primary">{e.category}</span>
                  <span className="text-xs text-foreground">{e.text}</span>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

const PERIODS = ["week", "month", "quarter", "year", "all"];

export function CompareTile({ compare, periodA, periodB, onA, onB }) {
  const t = useT();
  const a = compare?.a || {};
  const b = compare?.b || {};
  const d = compare?.delta || {};
  const Picker = ({ value, onPick, label }) => (
    <div className="mb-2">
      <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <div className="flex flex-wrap gap-1">
        {PERIODS.map((p) => (
          <button key={p} onClick={() => onPick(p)} className={`rounded-full px-2.5 py-1 text-xs font-medium ${value === p ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>
            {t(`an.period_${p}`)}
          </button>
        ))}
      </div>
    </div>
  );
  const rows = [
    { k: "gross", lbl: t("an.t11_gross"), va: fmtEur(a.gross_cents || 0), vb: fmtEur(b.gross_cents || 0), pct: d.gross_pct || 0 },
    { k: "bookings", lbl: t("an.t11_bookings"), va: String(a.bookings || 0), vb: String(b.bookings || 0), pct: d.bookings_pct || 0 },
    { k: "msgs", lbl: t("an.t11_msgs"), va: String(a.messages || 0), vb: String(b.messages || 0), pct: d.messages_pct || 0 },
    { k: "nc", lbl: t("an.t11_new_clients"), va: String(a.new_clients || 0), vb: String(b.new_clients || 0), pct: d.new_clients_pct || 0 },
  ];
  return (
    <div>
      <Picker value={periodA} onPick={onA} label={t("an.t11_period_a")} />
      <Picker value={periodB} onPick={onB} label={t("an.t11_period_b")} />
      <div className="mt-2 overflow-hidden rounded-xl border border-border">
        <div className="flex bg-secondary/60 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          <span className="flex-[1.4]">KPI</span><span className="flex-1 text-primary">A</span><span className="flex-1">B</span><span className="flex-[1.2]">Δ</span>
        </div>
        {rows.map((r) => (
          <div key={r.k} className="flex items-center border-t border-border px-3 py-2 text-xs">
            <span className="flex-[1.4] text-foreground">{r.lbl}</span>
            <span className="flex-1 font-medium text-primary">{r.va}</span>
            <span className="flex-1 text-muted-foreground">{r.vb}</span>
            <span className={`flex-[1.2] font-medium ${r.pct >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{r.pct >= 0 ? "▲" : "▼"} {Math.abs(r.pct).toFixed(1)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}
